import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareButton from "@/components/ShareButton";
import { toast } from "sonner";
import {
  Detective, ArrowLeft, MapPin, PaperPlaneRight, Sparkle, CheckCircle, XCircle, ChatCircleDots, GraduationCap, Bell, BellSlash,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RESPONSE_TYPES = [
  ["i_know_this_person", "I know this person"],
  ["i_may_know", "I may know this person"],
  ["same_class", "I was in the same class"],
  ["know_a_relative", "I know a family member"],
  ["have_contact", "I have their contact"],
  ["have_photo", "I have another photo"],
  ["have_information", "I have information"],
  ["can_connect", "I can connect you with someone"],
];
const LABEL = Object.fromEntries(RESPONSE_TYPES);

const DETAILS = [
  ["former_name", "Former name"], ["institution", "Institution"], ["course", "Course"],
  ["class_name", "Class"], ["grad_year", "Graduation year"], ["batch", "Batch"],
  ["last_known_location", "Last known location"], ["city", "City"], ["country", "Country"],
  ["profession", "Profession"], ["employer", "Employer"], ["known_friends", "Known friends"],
  ["known_relatives", "Known relatives"],
];

function Thread({ reqId, otherId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const boxRef = useRef();

  const load = () => api.get(`/find-requests/${reqId}/thread/${otherId}`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [reqId, otherId]);
  useEffect(() => { boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    const { data } = await api.post(`/find-requests/${reqId}/message`, { to_id: otherId, text: t });
    setMsgs((m) => [...m, data]);
  };

  return (
    <div className="mt-3 rounded-xl border border-border/60 bg-muted/20">
      <div ref={boxRef} className="max-h-56 space-y-2 overflow-y-auto p-3" data-testid="find-thread">
        {msgs.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">Start a private conversation.</p>}
        {msgs.map((m) => {
          const mine = m.from_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border/60"}`}>{m.text}</div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-border/60 p-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Private message…" className="rounded-full" data-testid="find-thread-input" />
        <Button type="submit" size="icon" className="h-9 w-9 shrink-0 rounded-full" data-testid="find-thread-send"><PaperPlaneRight size={16} weight="fill" /></Button>
      </form>
    </div>
  );
}

export default function FindRequestDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [matches, setMatches] = useState([]);
  const [responses, setResponses] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [rType, setRType] = useState("i_know_this_person");
  const [rMsg, setRMsg] = useState("");
  const [myResponse, setMyResponse] = useState(null);

  const load = () => api.get(`/find-requests/${slug}`).then((x) => setR(x.data)).catch(() => setR(false));
  useEffect(() => { load(); }, [slug]);

  useEffect(() => {
    if (!r || r === false) return;
    if (r.is_creator) {
      api.get(`/find-requests/${r.id}/matches`).then((x) => setMatches(x.data)).catch(() => {});
      api.get(`/find-requests/${r.id}/responses`).then((x) => setResponses(x.data)).catch(() => {});
    } else {
      api.get(`/find-requests/${r.id}/responses`).then((x) => setMyResponse(x.data[0] || null)).catch(() => {});
    }
  }, [r]);

  if (r === null) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (r === false) return <div className="p-12 text-center text-muted-foreground">Request not found.</div>;

  const submitResponse = async () => {
    try {
      await api.post(`/find-requests/${r.id}/respond`, { response_type: rType, message: rMsg });
      toast.success("Response sent to the requester");
      setRMsg("");
      api.get(`/find-requests/${r.id}/responses`).then((x) => setMyResponse(x.data[0] || null));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not respond"); }
  };

  const setStatus = async (status) => { await api.put(`/find-requests/${r.id}/status`, null, { params: { status } }); toast.success(`Marked ${status}`); load(); };

  const toggleMute = async () => {
    try {
      const { data } = await api.post(`/find-requests/${r.id}/mute`);
      setR((prev) => ({ ...prev, muted: data.muted }));
      toast.success(data.muted ? "Comment notifications muted" : "Comment notifications on");
    } catch (e) { toast.error("Could not update notifications"); }
  };

  const giveFeedback = async (candidateId, verdict) => {
    await api.post(`/find-requests/${r.id}/matches/${candidateId}/feedback`, { verdict });
    if (verdict === "rejected") {
      setMatches((m) => m.filter((x) => x.user_id !== candidateId));
      toast.success("Removed from matches");
    } else {
      setMatches((m) => m.map((x) => x.user_id === candidateId ? { ...x, feedback: "confirmed" } : x));
      toast.success("Marked as a confirmed match");
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <button onClick={() => nav("/find")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Reconnect</button>

      <Card className="border-border/60 p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {r.photo_file_id ? <img src={fileUrl(r.photo_file_id)} alt={r.subject_name} className="h-16 w-16 rounded-xl object-cover" />
              : <span className="grid h-16 w-16 place-items-center rounded-xl bg-primary text-primary-foreground"><Detective size={28} weight="duotone" /></span>}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl font-medium">Looking for {r.subject_name}</h1>
                <Badge variant={r.status === "open" ? "secondary" : "outline"} className="rounded-full font-normal capitalize">{r.status}</Badge>
              </div>
              {r.nickname && <p className="text-sm text-muted-foreground">Nickname "{r.nickname}"</p>}
              <p className="mt-1 text-xs text-muted-foreground">Posted by {r.creator_name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <ShareButton targetPath={`/find/r/${r.public_slug}`} entityType="find_request" entityId={r.id} title={`Find ${r.subject_name}`} />
            {r.is_creator && (
              <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={toggleMute} data-testid="find-mute-toggle">
                {r.muted ? <BellSlash size={15} /> : <Bell size={15} />} {r.muted ? "Muted" : "Mute"}
              </Button>
            )}
            {r.is_creator && r.status === "open" && <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setStatus("resolved")} data-testid="find-resolve"><CheckCircle size={15} /> Mark found</Button>}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {DETAILS.filter(([k]) => r[k]).map(([k, label]) => (
            <div key={k}><div className="overline text-muted-foreground">{label}</div><div className="text-sm">{r[k]}</div></div>
          ))}
        </div>
        {r.memories && <p className="mt-5 rounded-xl bg-muted/50 p-4 text-sm italic leading-relaxed">"{r.memories}"</p>}
        {r.other_clues && (
          <div className="mt-3">
            <div className="overline text-muted-foreground mb-1">Freeform notes</div>
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">{r.other_clues}</p>
          </div>
        )}
      </Card>

      {/* Non-creator: respond */}
      {!r.is_creator && (
        <Card className="mt-6 border-secondary/30 bg-secondary/5 p-6 shadow-sm">
          <h2 className="font-heading text-lg font-medium">Do you know {r.subject_name?.split(" ")[0]}?</h2>
          {myResponse ? (
            <div className="mt-3">
              <Badge variant="secondary" className="rounded-full">You responded: {LABEL[myResponse.response_type]}</Badge>
              <p className="mt-3 text-sm text-muted-foreground">Continue the conversation privately with {r.creator_name}:</p>
              <Thread reqId={r.id} otherId={r.creator_id} />
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label className="text-xs">Your response</Label>
                  <Select value={rType} onValueChange={setRType}>
                    <SelectTrigger data-testid="respond-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{RESPONSE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea rows={3} value={rMsg} onChange={(e) => setRMsg(e.target.value)} placeholder="Add a private message to the requester (optional)…" data-testid="respond-message" />
              <Button className="rounded-full gap-2" onClick={submitResponse} data-testid="respond-submit"><PaperPlaneRight size={15} weight="fill" /> Send response</Button>
              <p className="text-xs text-muted-foreground">Your contact details stay private — you'll chat through a secure in-app thread.</p>
            </div>
          )}
        </Card>
      )}

      {/* Creator: matches + responses */}
      {r.is_creator && (
        <>
          <Card className="mt-6 border-border/60 p-6 shadow-sm">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-medium"><Sparkle className="text-secondary" size={18} weight="fill" /> Possible matches</h2>
            {matches.length === 0 && <p className="text-sm text-muted-foreground">No strong matches found yet. Community responses may help.</p>}
            <div className="space-y-3">
              {matches.map((m) => {
                const initials = (m.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
                return (
                  <div key={m.user_id} className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 ${m.feedback === "confirmed" ? "border-secondary bg-secondary/5" : "border-border/60"}`} data-testid="match-item">
                    <Avatar className="h-11 w-11"><AvatarImage src={fileUrl(m.avatar_file_id)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <Link to={`/u/${m.public_slug || m.user_id}`} className="font-medium hover:underline">{m.name}</Link>
                      <div className="truncate text-xs text-muted-foreground">{m.headline || m.education?.[0]?.institution}{m.location ? ` · ${m.location}` : ""}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-lg font-semibold text-secondary">{m.confidence}%</div>
                      <div className="text-[10px] text-muted-foreground">match</div>
                    </div>
                    {m.feedback === "confirmed" ? (
                      <Badge className="gap-1 rounded-full bg-secondary text-secondary-foreground"><CheckCircle size={13} weight="fill" /> Confirmed</Badge>
                    ) : (
                      <div className="flex gap-1.5">
                        <Button size="sm" className="rounded-full gap-1.5" onClick={() => giveFeedback(m.user_id, "confirmed")} data-testid="match-confirm"><CheckCircle size={15} /> It's them</Button>
                        <Button size="icon" variant="outline" className="h-9 w-9 rounded-full text-muted-foreground" onClick={() => giveFeedback(m.user_id, "rejected")} data-testid="match-reject"><XCircle size={16} /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="mt-6 border-border/60 p-6 shadow-sm">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-medium"><ChatCircleDots className="text-secondary" size={18} weight="duotone" /> Responses ({responses.length})</h2>
            {responses.length === 0 && <p className="text-sm text-muted-foreground">No responses yet. Share this request to reach more people.</p>}
            <div className="space-y-3">
              {responses.map((resp) => {
                const initials = (resp.responder_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
                return (
                  <div key={resp.id} className="rounded-xl border border-border/60 p-4" data-testid="response-item">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10"><AvatarImage src={fileUrl(resp.responder_avatar)} /><AvatarFallback className="bg-accent text-xs">{initials}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{resp.responder_name}</div>
                        <Badge variant="secondary" className="rounded-full font-normal">{LABEL[resp.response_type]}</Badge>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setOpenThread(openThread === resp.responder_id ? null : resp.responder_id)} data-testid="response-message-btn"><ChatCircleDots size={15} /> Message</Button>
                    </div>
                    {resp.message && <p className="mt-2 text-sm text-foreground/90">{resp.message}</p>}
                    {openThread === resp.responder_id && <Thread reqId={r.id} otherId={resp.responder_id} />}
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </main>
  );
}
